/**
 * Roster Domain Type Definitions
 * All types for the V4 Roster (User Details) feature
 */

import { LightweightUser } from '../manage.slice'

export type SelectedUser = LightweightUser & {
  imageURL: string
  roles: string[]
  address: string
  city: string
  state: string
  zip: string
  phone: string
  nremtCertificationDate: string
  nremtCertificationNumber: string
}

// ============================================================================
// ACTIVITY TIMELINE TYPES
// Used by: user-activity-timeline.tsx, use-user-activity-timeline.tsx
// ============================================================================

export type RosterActivityType =
  | 'certificate-recorded'
  | 'certificate-live'
  | 'certificate-external'
  | 'assignment-created'
  | 'assignment-completed'
  | 'event-registered'
  | 'event-attended'
  | 'event-completed'
  | 'credential-uploaded'
  | 'credential-expiring'
  | 'credential-expired'
  | 'license-uploaded'
  | 'license-expiring'
  | 'license-expired'

/**
 * Base activity interface
 */
export interface RosterBaseActivity {
  id: string
  userID: string
  timestamp: string
  activityType: RosterActivityType
}

/**
 * Certificate Activity Data
 * Used by: ce-activity-modal.tsx, user-activity-timeline.tsx
 */
export interface RosterCertificateActivity extends RosterBaseActivity {
  activityType:
    'certificate-recorded' | 'certificate-live' | 'certificate-external'
  certificateID: string
  courseID?: string
  courseName: string
  courseCode?: string
  completionDate: string
  ceHours: number
  category: string
  licenseTypes: string[]
  assignmentID?: string
  assignmentName?: string
  certificateFileName?: string
  isDownloadable: boolean
}

/**
 * Assignment Activity Data
 * Used by: assignment-activity-modal.tsx, user-activity-timeline.tsx
 */
export interface RosterAssignmentActivity extends RosterBaseActivity {
  activityType: 'assignment-created' | 'assignment-completed'
  assignmentID: string
  assignmentName: string
  dueDate?: string
  courseCount: number
  totalCeHours: number
  completionPercentage: number
  description?: string
}

/**
 * Event Activity Data
 * Used by: user-action-modal.tsx, user-activity-timeline.tsx
 */
export interface RosterEventActivity extends RosterBaseActivity {
  activityType: 'event-registered' | 'event-attended' | 'event-completed'
  eventID: string
  eventName: string
  eventDate: string
  eventType: string
  sessionID?: string
  attendanceStatus?: 'registered' | 'attended' | 'completed' | 'no-show'
  certificateAvailable?: boolean
}

/**
 * Credential Activity Data
 * Used by: credential-activity-modal.tsx, user-activity-timeline.tsx
 */
export interface RosterCredentialActivity extends RosterBaseActivity {
  activityType:
    'credential-uploaded' | 'credential-expiring' | 'credential-expired'
  credentialID: string
  credentialName: string
  credentialType: string
  number?: string
  state?: string
  expirationDate?: string
  daysToExpiration?: number
  status: 'active' | 'expiring-soon' | 'expired'
  fileName?: string
}

/**
 * License Activity Data
 * Used by: license-activity-modal.tsx, user-activity-timeline.tsx
 */
export interface RosterLicenseActivity extends RosterBaseActivity {
  activityType: 'license-uploaded' | 'license-expiring' | 'license-expired'
  licenseID: string
  licenseType: string
  licenseName: string
  number?: string
  state?: string
  expirationDate?: string
  daysToExpiration?: number
  status: 'active' | 'expiring-soon' | 'expired'
  fileName?: string
}

/**
 * Union type for all activity data
 */
export type RosterUserActivityData =
  | RosterCertificateActivity
  | RosterAssignmentActivity
  | RosterEventActivity
  | RosterCredentialActivity
  | RosterLicenseActivity

/**
 * Timeline Event from API
 * Used by: user-activity-timeline.tsx, use-user-activity-timeline.tsx
 */
export interface RosterUserActivityEvent {
  id: string
  title: string
  description?: string
  timestamp: string
  type: RosterActivityType
  metadata: { key: string; value: string }[]
  actions?: { label: string; action: string; variant?: string }[]
  data: RosterUserActivityData
}

/**
 * API Response structure
 * Used by: use-user-activity-timeline.tsx
 */
export interface RosterUserActivityResponse {
  activities?: RosterUserActivityEvent[]
  events?: RosterUserActivityEvent[]
  pagination: {
    total: number
    page: number
    limit: number
    hasNext: boolean
  }
}

/**
 * Activity color mapping type
 * Used by: user-activity-timeline.tsx
 */
export interface RosterActivityColorMap {
  'certificate-recorded': string
  'certificate-live': string
  'certificate-external': string
  'assignment-created': string
  'assignment-completed': string
  'event-registered': string
  'event-attended': string
  'event-completed': string
  'credential-uploaded': string
  'credential-expiring': string
  'credential-expired': string
  'license-uploaded': string
  'license-expiring': string
  'license-expired': string
  'credential-activity': string
  'license-activity': string
}

// ============================================================================
// TIMELINE FILTER TYPES
// Used by: user-timeline-filter-panel.tsx, use-user-timeline-filters.ts
// ============================================================================

export type RosterTimelineFilterRange =
  'week' | 'month' | 'quarter' | 'year' | 'custom'

/**
 * Filter state for user activity timeline
 * Used by: user-timeline-filter-panel.tsx, use-user-timeline-filters.ts
 */
export interface RosterUserTimelineFilters {
  timeRange: RosterTimelineFilterRange
  customStartDate: string | null
  customEndDate: string | null
  searchKeyword: string
  selectedCategories: string[]
  selectedActivityTypes: string[]
  showLicenseCredentialEvents: boolean
}

/**
 * Query parameters for API calls
 * Used by: use-user-activity-timeline.tsx
 */
export interface RosterUserTimelineQueryParams {
  startDate: string
  endDate: string
  searchKeyword?: string
  categories?: string[]
  activityTypes?: string[]
  showLicenseCredentialEvents?: boolean
}

/**
 * Activity type options for filtering
 * Used by: user-timeline-filter-panel.tsx
 */
export interface RosterActivityTypeOption {
  value: string
  label: string
}

// ============================================================================
// CREDENTIALS & LICENSES DATA GRID TYPES
// Used by: credentials-data-grid.tsx, user-details.tsx
// ============================================================================

/**
 * Credentials/License row item for data grid
 * Roster-specific type with id for download functionality
 */
export interface RosterCredentialItem {
  id: string // DataGrid row ID (backend ID for both credentials and licenses)
  category: 'License' | 'Credential'
  type: string // Properly formatted (e.g., "BLS", "Paramedic")
  number: string
  fileName?: string
  fileUrl?: string
  verificationStatus?: 'accepted' | 'rejected' | 'pending' // Only for credentials when verification enabled
  state?: string // Only for licenses
  issueDate?: string
  expiration?: string // ISO date
  status: 'active' | 'expiring-soon' | 'expired'
  daysToExpiration?: number
  dateAdded: string // ISO date
}

// ============================================================================
// MODAL TYPES (adapted from dashboard types for roster domain isolation)
// ============================================================================

/**
 * CE Activity Data for Modal
 * Used by: ce-activity-modal.tsx
 */
export interface RosterCEActivityData {
  activityType: string // 'ce-completion'
  certificateID: string
  userID: string
  courseId: string
  assignmentId?: string | null
  userName: string
  userEmail: string
  courseTitle: string
  courseName: string
  courseCode?: string | null
  completionDate: string
  ceHours: number
  category: string
  licenseTypes: string[]
  assignmentName?: string | null
  wasAssigned: boolean
  assignmentDueDate?: string | null
  hasCertificate: boolean
  certificateFileName?: string | null
  section?: string | null
  courseType?: string // 'Recorded', 'Live', or 'External - Recorded', 'External - Live'
}

/**
 * Assignment Event Data for Modal
 * Used by: assignment-activity-modal.tsx
 */
export interface RosterAssignmentEventData {
  activityType: string // 'assignment-created'
  assignmentID: string
  assignmentName: string
  createdDate: string
  dueDate: string
  userCount: number
  courseCount: number
  totalCeHours: number
  overallCompletionPercentage: number
  assignedUserNames: string[]
  assignedCourseNames: string[]
  description?: string
}

/**
 * User Action Data for Modal
 * Used by: user-action-modal.tsx
 */
export interface RosterUserActionData {
  activityType: string // 'user-action'
  actionID: string
  eventID: string
  userID: string
  userName: string
  userEmail: string
  eventName: string
  actionType: string // 'event-attended' | 'event-completed'
  actionDate: string
  sessionID?: string | null
}

/**
 * License Activity Data for Modal
 * Used by: license-activity-modal.tsx
 */
export interface RosterLicenseActivityData {
  activityType: 'license-activity'
  licenseID: string
  userID: string
  userName: string
  userEmail: string
  licenseName: string // License type (e.g., "EMT", "Paramedic")
  licenseNumber: string
  licenseState: string // State where license is issued
  expiration?: string // ISO date string (optional for some licenses)
  actionType: string // "added" | "expiring-soon" | "expired"
  actionDate: string // ISO date string
  daysToExpiration?: number // Negative for expired, positive for active
}

/**
 * Credential Activity Data for Modal
 * Used by: credential-activity-modal.tsx
 */
export interface RosterCredentialActivityData {
  activityType: 'credential-activity'
  credentialID: string
  userID: string
  userName: string
  userEmail: string
  credentialName: string // Credential type (e.g., "BLS", "ACLS", "PALS")
  credentialNumber?: string // Optional credential ID number
  expiration: string // ISO date string (required for credentials)
  actionType: string // "added" | "expiring-soon" | "expired"
  actionDate: string // ISO date string
  daysToExpiration?: number // Negative for expired, positive for active
}

// ============================================================================
// TYPE GUARDS
// Used by: user-activity-timeline.tsx, modals
// ============================================================================

export function isRosterCertificateActivity(
  data: RosterUserActivityData,
): data is RosterCertificateActivity {
  return data?.activityType?.startsWith('certificate-') || false
}

export function isRosterAssignmentActivity(
  data: RosterUserActivityData,
): data is RosterAssignmentActivity {
  return data?.activityType?.startsWith('assignment-') || false
}

export function isRosterEventActivity(
  data: RosterUserActivityData,
): data is RosterEventActivity {
  return data?.activityType?.startsWith('event-') || false
}

export function isRosterCredentialActivity(
  data: RosterUserActivityData,
): data is RosterCredentialActivity {
  return data?.activityType?.startsWith('credential-') || false
}

export function isRosterLicenseActivity(
  data: RosterUserActivityData,
): data is RosterLicenseActivity {
  return data?.activityType?.startsWith('license-') || false
}

// ============================================================================
// CREDENTIAL MODAL SHARED TYPES
// Used by: create-credential-modal.tsx, edit-credential-modal.tsx
// ============================================================================

/**
 * File data structure for credential file uploads
 */
export type CredentialFileData = {
  name: string
  type: string
  extension: string
  file: Blob
}

export type ExternalCertificatePayload = {
  userID: string
  linkedServiceID: string
  id?: string
  name: string
  courseCode: string
  length: number
  hourType: string
  courseType: string
  dateCompleted: string
}

export type ExternalCertificateImagePayload = {
  id: string
  imageType: string
}

/**
 * Available credential types
 */
export const CREDENTIAL_TYPES = [
  'BLS',
  'ACLS',
  'PALS',
  'NRP',
  'PHTLS',
  'CCP-C',
  'FP-C',
  'CFRN',
  'Other',
] as const
